import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";

// Drop-in replacement for a native <select>. The browser decides which way a
// native list opens and flips it upward when the page is short below; this one
// always opens under the field. It is portalled to <body> so a scrolling parent
// cannot clip it, and it emits the same { target: { name, value } } event.
const SEARCH_THRESHOLD = 10;
const GAP = 4;
const MAX_LIST_HEIGHT = 288;
const MIN_LIST_HEIGHT = 120;

const normalize = (opt) =>
  typeof opt === "object" && opt !== null
    ? { value: opt.value, label: opt.label ?? String(opt.value), disabled: !!opt.disabled }
    : { value: opt, label: String(opt), disabled: false };

const SelectMenu = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  options = [],
  placeholder = "Select...",
  disabled = false,
  clearable = false,
  searchable,
  className = "",
  placeholderClassName = "text-gray-400",
  menuMinWidth = 180,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  const items = useMemo(() => options.map(normalize), [options]);
  const showSearch = searchable ?? items.length > SEARCH_THRESHOLD;
  const selected = items.find((o) => String(o.value) === String(value ?? ""));

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? items.filter((o) => o.label.toLowerCase().includes(q)) : items;
    return clearable && !q ? [{ value: "", label: placeholder, isPlaceholder: true }, ...filtered] : filtered;
  }, [items, query, clearable, placeholder]);

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom - GAP - 8;
    setPosition({
      top: rect.bottom + GAP,
      left: rect.left,
      width: Math.max(rect.width, menuMinWidth),
      maxHeight: Math.min(MAX_LIST_HEIGHT, Math.max(below, MIN_LIST_HEIGHT)),
    });
  }, [menuMinWidth]);

  const openMenu = () => {
    if (disabled) return;
    // Bring a field near the bottom edge up first, so there is room under it.
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect && window.innerHeight - rect.bottom < 200) {
      triggerRef.current.scrollIntoView({ block: "center" });
    }
    place();
    setQuery("");
    setActive(Math.max(0, visible.findIndex((o) => String(o.value) === String(value ?? ""))));
    setOpen(true);
  };

  // Blur handlers validate e.target.value, so after a pick they must see the
  // new value rather than the prop, which has not re-rendered yet.
  const close = useCallback(
    (refocus = false, current = value ?? "") => {
      setOpen(false);
      setQuery("");
      onBlur?.({ target: { name, value: String(current) } });
      if (refocus) triggerRef.current?.focus();
    },
    [name, value, onBlur]
  );

  const choose = (option) => {
    if (!option || option.disabled) return;
    const next = String(option.value);
    const target = { name, value: next, type: "select-one" };
    onChange?.({ target, currentTarget: target, preventDefault() {}, persist() {} });
    close(true, next);
  };

  useLayoutEffect(() => {
    if (!open) return;
    if (showSearch) searchRef.current?.focus();
    else listRef.current?.focus();
  }, [open, showSearch]);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      close();
    };
    // Capture catches scrolls in any ancestor, not just the window.
    const handleScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      place();
    };
    document.addEventListener("mousedown", handlePointer);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", place);
    };
  }, [open, close, place]);

  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const move = (step) => {
    if (!visible.length) return;
    let next = active;
    for (let i = 0; i < visible.length; i++) {
      next = (next + step + visible.length) % visible.length;
      if (!visible[next].disabled) break;
    }
    setActive(next);
  };

  const handleMenuKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(visible[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    } else if (e.key === "Tab") {
      // The menu lives at the end of <body>, so let Tab resume from the field.
      e.preventDefault();
      close(true);
    }
  };

  const handleTriggerKeyDown = (e) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
      e.preventDefault();
      openMenu();
    }
  };

  const listboxId = `${id || name || "select"}-listbox`;

  return (
    <>
      <button
        ref={triggerRef}
        id={id || name}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={handleTriggerKeyDown}
        onBlur={(e) => {
          if (!open && !menuRef.current?.contains(e.relatedTarget)) {
            onBlur?.({ target: { name, value: String(value ?? "") } });
          }
        }}
        className={`relative flex w-full items-center text-left ${className}`}
        {...props}
      >
        <span className={`block flex-1 truncate ${selected ? "" : placeholderClassName}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transition-transform ${
            open ? "rotate-180" : ""
          } ${disabled ? "text-gray-300" : "text-gray-500"}`}
        />
      </button>
      {name && <input type="hidden" name={name} value={value ?? ""} />}

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            data-select-menu=""
            onKeyDown={handleMenuKeyDown}
            style={{ top: position.top, left: position.left, width: position.width }}
            className="fixed z-[1000] flex flex-col rounded-lg border border-gray-200 bg-white shadow-pop overflow-hidden"
          >
            {showSearch && (
              <div className="relative shrink-0 border-b border-gray-100">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  placeholder="Search..."
                  className="w-full bg-transparent py-2 pl-8 pr-3 text-[13px] text-gray-900 outline-none placeholder:text-gray-400"
                />
              </div>
            )}

            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              tabIndex={-1}
              style={{ maxHeight: position.maxHeight }}
              className="overflow-y-auto py-1 outline-none"
            >
              {visible.length === 0 ? (
                <li className="px-3 py-2 text-[13px] text-gray-400">No matches</li>
              ) : (
                visible.map((option, index) => {
                  const isSelected = !option.isPlaceholder && option === selected;
                  return (
                    <li
                      key={`${option.value}-${index}`}
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled || undefined}
                      onMouseEnter={() => setActive(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => choose(option)}
                      className={`flex items-center justify-between gap-2 px-3 py-1.5 text-[13px] ${
                        option.disabled
                          ? "cursor-not-allowed text-gray-300"
                          : `cursor-pointer ${
                              index === active ? "bg-brand-50 text-brand-800" : "text-gray-700"
                            }`
                      } ${option.isPlaceholder ? "text-gray-400" : ""} ${
                        isSelected ? "font-medium" : ""
                      }`}
                    >
                      <span className="truncate">{option.label}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 shrink-0 text-brand-600" />}
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          document.body
        )}
    </>
  );
};

export default SelectMenu;
